import { z } from 'zod'
import { apiRequest, apiRequestMultipart } from '@/lib/api-client'

export const languageVariantSchema = z.enum(['de-CH', 'de-DE', 'en-GB', 'en-US'])
export type LanguageVariant = z.infer<typeof languageVariantSchema>

export const brandTypeSchema = z.enum(['person', 'company', 'persona'])
export type BrandType = z.infer<typeof brandTypeSchema>

export const brandIdentitySourceTypeSchema = z.enum([
  'logo',
  'color_swatch',
  'font_sample',
  'presentation',
  'website',
  'reference_doc',
])
export type BrandIdentitySourceType = z.infer<typeof brandIdentitySourceTypeSchema>

export const brandAssetTypeSchema = z.enum([
  'logo_primary',
  'logo_inverse',
  'logo_icon',
  'gradient',
  'texture',
  'background_pattern',
  'icon_set',
  'custom_image',
])
export type BrandAssetType = z.infer<typeof brandAssetTypeSchema>

export const brandStyleguideSchema = z
  .object({
    summary: z.string().optional(),
    colors: z
      .array(
        z.object({
          name: z.string(),
          hex: z.string(),
          usage: z.string().optional(),
        }),
      )
      .default([]),
    typography: z
      .object({
        heading: z.string().optional(),
        body: z.string().optional(),
        notes: z.string().optional(),
      })
      .partial()
      .default({}),
    visual_style: z.string().optional(),
    voice: z.string().optional(),
    logo_usage: z.string().optional(),
    gradient_style: z.string().optional(),
    presentation_style: z.string().optional(),
    imagery: z.string().optional(),
    layout_rules: z.array(z.string()).default([]),
    do_nots: z.array(z.string()).default([]),
    confidence: z.number().optional(),
    source_count: z.number().optional(),
    last_updated: z.string().optional(),
    last_edited_by_user_at: z.string().optional(),
  })
  .partial()
export type BrandStyleguide = z.infer<typeof brandStyleguideSchema>

export const topPostExampleSchema = z.object({
  platform: z.enum(['linkedin', 'x']),
  content: z.string(),
  engagement_metrics: z
    .object({
      likes: z.number().int().nonnegative().optional(),
      comments: z.number().int().nonnegative().optional(),
      shares: z.number().int().nonnegative().optional(),
    })
    .optional(),
  created_at: z.string().optional(),
})
export type TopPostExample = z.infer<typeof topPostExampleSchema>

export const brandProfileSchema = z.object({
  id: z.string(),
  workspace_id: z.string(),
  slug: z.string(),
  name: z.string(),
  brand_type: brandTypeSchema.default('company'),
  domain_url: z.string().nullable().optional(),
  speaker_role: z.string().nullable().optional(),
  default_model: z.string().nullable().optional(),
  brand_styleguide: brandStyleguideSchema.default({}),
  is_default: z.boolean(),
  color_primary: z.string().nullable(),
  color_accent: z.string().nullable(),
  color_neutral: z.string().nullable(),
  typography: z.record(z.unknown()).default({}),
  tone: z.string().nullable(),
  do_nots: z.array(z.string()).default([]),
  channel_rules: z.record(z.unknown()).default({}),
  user_bio: z.string().nullable(),
  company_description: z.string().nullable(),
  logo_url: z.string().nullable(),
  gradient_url: z.string().nullable(),
  swatch_urls: z.array(z.object({ name: z.string(), color: z.string(), url: z.string() })).default([]),
  // Migration 004 — Felder duerfen vor Migration fehlen, deshalb optional + nullable
  linkedin_profile_url: z.string().nullable().optional(),
  linkedin_bio: z.string().nullable().optional(),
  x_handle: z.string().nullable().optional(),
  top_posts_examples: z.array(topPostExampleSchema).optional().default([]),
  language_variant: languageVariantSchema.optional(),
  // Migration 008 — konsolidierte Voice-Analyse. Default {} bei alten Rows.
  voice_analysis: z
    .object({
      tone: z.string().optional(),
      do_nots: z.array(z.string()).optional(),
      structural_patterns: z.string().optional(),
      hook_examples: z.array(z.string()).optional(),
      channel_rules: z
        .object({
          linkedin: z.string().optional(),
          x: z.string().optional(),
          blog: z.string().optional(),
          email: z.string().optional(),
        })
        .partial()
        .optional(),
      usps: z.array(z.string()).optional(),
      language_variant: languageVariantSchema.optional(),
      confidence: z.number().optional(),
      last_updated: z.string().optional(),
      source_count: z.number().optional(),
    })
    .partial()
    .optional()
    .default({}),
  created_at: z.string(),
  updated_at: z.string(),
})
export type BrandProfile = z.infer<typeof brandProfileSchema>

const profilesListSchema = z.object({ profiles: z.array(brandProfileSchema) })

export const brandIdentitySourceSchema = z.object({
  id: z.string(),
  workspace_id: z.string(),
  brand_profile_id: z.string(),
  source_type: brandIdentitySourceTypeSchema,
  source_name: z.string(),
  storage_path: z.string().nullable(),
  source_url: z.string().nullable(),
  file_mime: z.string().nullable(),
  raw_text: z.string().nullable(),
  extracted_styleguide: brandStyleguideSchema.default({}),
  status: z.enum(['uploaded', 'analyzing', 'analyzed', 'error']),
  error_message: z.string().nullable(),
  uploaded_at: z.string(),
  analyzed_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})
export type BrandIdentitySource = z.infer<typeof brandIdentitySourceSchema>

const identitySourcesListSchema = z.object({ items: z.array(brandIdentitySourceSchema) })
const styleguideResponseSchema = z.object({ brand_styleguide: brandStyleguideSchema })

export const brandAssetSchema = z.object({
  id: z.string(),
  workspace_id: z.string(),
  brand_id: z.string(),
  asset_type: brandAssetTypeSchema,
  slot_role: z.string().nullable(),
  storage_path: z.string(),
  mime_type: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  display_order: z.number(),
  metadata: z.record(z.unknown()).default({}),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  proxy_url: z.string(),
})
export type BrandAsset = z.infer<typeof brandAssetSchema>

const brandAssetsListSchema = z.object({ assets: z.array(brandAssetSchema) })
const successSchema = z.object({ success: z.boolean() })

export interface BrandCreateInput {
  slug: string
  name: string
  brand_type?: BrandType
  domain_url?: string
  speaker_role?: string
  default_model?: string
  brand_styleguide?: BrandStyleguide
  is_default?: boolean
  color_primary?: string
  color_accent?: string
  color_neutral?: string
  typography?: Record<string, unknown>
  tone?: string
  do_nots?: string[]
  channel_rules?: Record<string, unknown>
  user_bio?: string
  company_description?: string
  linkedin_profile_url?: string
  linkedin_bio?: string
  x_handle?: string
  language_variant?: LanguageVariant
}

export type BrandUpdateInput = Partial<BrandCreateInput>

export async function listBrandProfiles(): Promise<BrandProfile[]> {
  const result = await apiRequest({
    path: '/brand/profiles',
    schema: profilesListSchema,
  })
  return result.profiles
}

export async function createBrandProfile(input: BrandCreateInput): Promise<BrandProfile> {
  return await apiRequest({
    method: 'POST',
    path: '/brand/profiles',
    body: input,
    schema: brandProfileSchema,
  })
}

export async function updateBrandProfile(id: string, input: BrandUpdateInput): Promise<BrandProfile> {
  return await apiRequest({
    method: 'PATCH',
    path: `/brand/profiles/${id}`,
    body: input,
    schema: brandProfileSchema,
  })
}

export async function deleteBrandProfile(id: string): Promise<void> {
  await apiRequest({
    method: 'DELETE',
    path: `/brand/profiles/${id}`,
    schema: z.object({ success: z.boolean() }),
  })
}

export async function uploadBrandAsset(
  id: string,
  kind: 'logo' | 'gradient',
  file: File,
): Promise<BrandProfile> {
  const formData = new FormData()
  formData.append('file', file)
  return await apiRequestMultipart({
    path: `/brand/profiles/${id}/upload-${kind}`,
    formData,
    schema: brandProfileSchema,
  })
}

export async function uploadBrandSwatch(
  id: string,
  name: string,
  color: string,
  file: File,
): Promise<BrandProfile> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('name', name)
  formData.append('color', color)
  return await apiRequestMultipart({
    path: `/brand/profiles/${id}/upload-swatch`,
    formData,
    schema: brandProfileSchema,
  })
}

export async function listBrandAssets(brandId: string): Promise<BrandAsset[]> {
  const result = await apiRequest({
    path: `/brands/${brandId}/assets`,
    schema: brandAssetsListSchema,
  })
  return result.assets
}

export async function uploadManagedBrandAsset(
  brandId: string,
  input: {
    file: File
    assetType: BrandAssetType
    slotRole?: string
    displayOrder?: number
  },
): Promise<BrandAsset> {
  const formData = new FormData()
  formData.append('file', input.file)
  formData.append('asset_type', input.assetType)
  if (input.slotRole) formData.append('slot_role', input.slotRole)
  if (input.displayOrder !== undefined) formData.append('display_order', String(input.displayOrder))
  return await apiRequestMultipart({
    path: `/brands/${brandId}/assets`,
    formData,
    schema: brandAssetSchema,
  })
}

export async function updateManagedBrandAsset(
  brandId: string,
  assetId: string,
  input: { slotRole?: string | null; displayOrder?: number },
): Promise<BrandAsset> {
  return await apiRequest({
    method: 'PATCH',
    path: `/brands/${brandId}/assets/${assetId}`,
    body: {
      ...(input.slotRole !== undefined ? { slot_role: input.slotRole } : {}),
      ...(input.displayOrder !== undefined ? { display_order: input.displayOrder } : {}),
    },
    schema: brandAssetSchema,
  })
}

export async function deleteManagedBrandAsset(brandId: string, assetId: string): Promise<void> {
  await apiRequest({
    method: 'DELETE',
    path: `/brands/${brandId}/assets/${assetId}`,
    schema: successSchema,
  })
}

export async function listBrandIdentitySources(id: string): Promise<BrandIdentitySource[]> {
  const result = await apiRequest({
    path: `/brand/profiles/${id}/identity-sources`,
    schema: identitySourcesListSchema,
  })
  return result.items
}

export async function uploadBrandIdentitySource(
  id: string,
  input: {
    file: File
    sourceType: Exclude<BrandIdentitySourceType, 'website'>
    sourceName?: string
    skipAnalyze?: boolean
  },
): Promise<BrandIdentitySource> {
  const formData = new FormData()
  formData.append('file', input.file)
  formData.append('source_type', input.sourceType)
  if (input.sourceName) formData.append('source_name', input.sourceName)
  const path = input.skipAnalyze
    ? `/brand/profiles/${id}/identity-sources?skip_analyze=true`
    : `/brand/profiles/${id}/identity-sources`
  return await apiRequestMultipart({
    path,
    formData,
    schema: brandIdentitySourceSchema,
  })
}

export async function crawlBrandIdentityWebsite(
  id: string,
  input: { url: string; depth?: number; skipAnalyze?: boolean },
): Promise<BrandIdentitySource> {
  return await apiRequest({
    method: 'POST',
    path: `/brand/profiles/${id}/identity-sources/website`,
    body: input,
    schema: brandIdentitySourceSchema,
  })
}

export async function consolidateBrandStyleguide(id: string): Promise<BrandStyleguide> {
  const result = await apiRequest({
    method: 'POST',
    path: `/brand/profiles/${id}/consolidate-styleguide`,
    schema: styleguideResponseSchema,
  })
  return result.brand_styleguide
}

export async function updateBrandStyleguide(
  id: string,
  input: BrandStyleguide,
): Promise<BrandStyleguide> {
  const result = await apiRequest({
    method: 'PATCH',
    path: `/brand/profiles/${id}/styleguide`,
    body: input,
    schema: styleguideResponseSchema,
  })
  return result.brand_styleguide
}
